import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Shield, FileText, Globe } from "lucide-react";
import { Button } from "../components/ui/button";
import SEO, { createBreadcrumbSchema } from "../components/SEO";

export default function PrivacidadePage() {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'pt';
  const lastUpdate = currentLang === 'en' ? "January 23, 2026" : "23 de Janeiro de 2026";

  // Language-aware paths
  const paths = {
    home: '/',
    about: currentLang === 'en' ? '/en/about' : '/pt/sobre',
    terms: currentLang === 'en' ? '/en/terms-of-use' : '/pt/termos-de-uso',
    contact: currentLang === 'en' ? '/en/contact' : '/pt/contato',
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  // SEO Configuration
  const seoConfig = currentLang === 'en' ? {
    title: "Privacy Policy - OmbudNow",
    description: "OmbudNow Privacy Policy. Learn how we collect, use, and protect your personal data in compliance with LGPD and international privacy regulations.",
    canonical: "/en/privacy-policy"
  } : {
    title: "Política de Privacidade - OmbudNow",
    description: "Política de Privacidade da OmbudNow. Saiba como coletamos, usamos e protegemos seus dados pessoais em conformidade com a LGPD.",
    canonical: "/pt/politica-de-privacidade"
  };

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: currentLang === 'en' ? 'Home' : 'Início', url: '/' },
    { name: currentLang === 'en' ? 'Privacy Policy' : 'Política de Privacidade', url: seoConfig.canonical }
  ]);

  const content = currentLang === 'en' ? {
    badge: "LGPD Compliant",
    title: "Privacy Policy",
    lastUpdate: "Last updated",
    sections: [
      {
        title: "1. Introduction",
        content: "OmbudNow is committed to protecting your privacy and ensuring the security of your personal data. This Privacy Policy describes how we collect, use, store, and protect information in accordance with Brazil's General Data Protection Law (LGPD - Law 13.709/2018) and other applicable data protection regulations."
      },
      {
        title: "2. Data We Collect",
        content: "We collect the following categories of personal data: Identification data (name, email, CPF/CNPJ); Contact information (phone, address); Company data (company name, segment, number of employees); Access and usage data (logs, IP, device information); Report and ombudsman data submitted through the platform."
      },
      {
        title: "3. Purpose of Data Processing",
        content: "Your data is processed for the following purposes: Provision and improvement of our services; User authentication and security; Communication about updates and support; Compliance with legal and regulatory obligations; Statistical analysis and service enhancement."
      },
      {
        title: "4. Data Sharing",
        content: "We do not sell your personal data. We may share data with: Service providers who assist in platform operation; Legal authorities when required by law; Third parties only with your explicit consent."
      },
      {
        title: "5. Your Rights",
        content: "In accordance with LGPD, you have the right to: Access your personal data; Correct incomplete or inaccurate data; Request anonymization, blocking, or deletion; Data portability; Withdraw consent at any time; Request information about sharing."
      },
      {
        title: "6. Data Security",
        content: "We implement technical and organizational security measures including: End-to-end encryption; Access controls and authentication; Continuous monitoring and auditing; Incident response procedures; Staff training on data protection."
      },
      {
        title: "7. Contact",
        content: "For questions about this policy or to exercise your rights, contact us at: support@ombudnow.com"
      }
    ]
  } : {
    badge: "Conformidade LGPD",
    title: "Política de Privacidade",
    lastUpdate: "Última atualização",
    sections: [
      {
        title: "1. Introdução",
        content: "A OmbudNow está comprometida em proteger sua privacidade e garantir a segurança de seus dados pessoais. Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e protegemos informações de acordo com a Lei Geral de Proteção de Dados (LGPD - Lei 13.709/2018) e demais regulamentações aplicáveis."
      },
      {
        title: "2. Dados que Coletamos",
        content: "Coletamos as seguintes categorias de dados pessoais: Dados de identificação (nome, email, CPF/CNPJ); Informações de contato (telefone, endereço); Dados da empresa (razão social, segmento, número de funcionários); Dados de acesso e uso (logs, IP, informações do dispositivo); Dados de denúncias e ouvidoria submetidos na plataforma."
      },
      {
        title: "3. Finalidade do Tratamento",
        content: "Seus dados são tratados para as seguintes finalidades: Prestação e melhoria dos nossos serviços; Autenticação e segurança do usuário; Comunicação sobre atualizações e suporte; Cumprimento de obrigações legais e regulatórias; Análises estatísticas e aprimoramento do serviço."
      },
      {
        title: "4. Compartilhamento de Dados",
        content: "Não vendemos seus dados pessoais. Podemos compartilhar dados com: Prestadores de serviços que auxiliam na operação da plataforma; Autoridades legais quando exigido por lei; Terceiros somente com seu consentimento explícito."
      },
      {
        title: "5. Seus Direitos",
        content: "De acordo com a LGPD, você tem direito a: Acessar seus dados pessoais; Corrigir dados incompletos ou inexatos; Solicitar anonimização, bloqueio ou eliminação; Portabilidade dos dados; Revogar consentimento a qualquer momento; Solicitar informações sobre compartilhamento."
      },
      {
        title: "6. Segurança dos Dados",
        content: "Implementamos medidas de segurança técnicas e organizacionais incluindo: Criptografia de ponta a ponta; Controles de acesso e autenticação; Monitoramento e auditoria contínuos; Procedimentos de resposta a incidentes; Treinamento de equipe em proteção de dados."
      },
      {
        title: "7. Contato",
        content: "Para dúvidas sobre esta política ou para exercer seus direitos, entre em contato pelo email: support@ombudnow.com"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white font-ibm">
      <SEO
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={seoConfig.canonical}
        lang={currentLang}
        schemas={[breadcrumbSchema]}
      />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to={paths.home} className="flex items-center gap-3">
              <div className="w-9 h-9 bg-slate-900 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-slate-900 font-chivo tracking-tight">OMBUDNOW</span>
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to={currentLang === 'en' ? '/en/about' : '/pt/sobre'} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">
                {currentLang === 'en' ? 'About' : 'Sobre'}
              </Link>
              <Link to={currentLang === 'en' ? '/en/blog' : '/pt/blog'} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">
                Blog
              </Link>
              <Link to={currentLang === 'en' ? '/en/contact' : '/pt/contato'} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">
                {currentLang === 'en' ? 'Contact' : 'Contato'}
              </Link>
              <div className="flex items-center gap-1.5 text-slate-500 text-sm border-l border-slate-200 pl-6 ml-2">
                <Globe className="w-4 h-4" />
                <button 
                  onClick={() => changeLanguage('pt')} 
                  className={`px-1 ${currentLang === 'pt' ? 'text-slate-900 font-semibold' : 'text-slate-500 hover:text-slate-700'} transition-colors`}
                >
                  PT
                </button>
                <span className="text-slate-300">/</span>
                <button 
                  onClick={() => changeLanguage('en')} 
                  className={`px-1 ${currentLang === 'en' ? 'text-slate-900 font-semibold' : 'text-slate-500 hover:text-slate-700'} transition-colors`}
                >
                  EN
                </button>
              </div>
            </nav>

            <Button
              className="bg-slate-900 text-white hover:bg-slate-800 font-medium px-5"
              onClick={() => window.location.href = '/onboarding?plan=free'}
            >
              {currentLang === 'en' ? 'Start free' : 'Começar grátis'}
            </Button>
          </div>
        </div>
      </header>

      <main className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-600 px-4 py-2 rounded-full text-sm font-medium mb-4 border border-blue-100">
              <FileText className="w-4 h-4" />
              {content.badge}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 font-chivo tracking-tight">
              {content.title}
            </h1>
            <p className="text-slate-500">
              {content.lastUpdate}: {lastUpdate}
            </p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {content.sections.map((section, index) => (
              <div key={index} className={`p-8 ${index !== content.sections.length - 1 ? 'border-b border-slate-100' : ''}`}>
                <h2 className="text-xl font-bold text-slate-900 mb-4 font-chivo">{section.title}</h2>
                <p className="text-slate-600 leading-relaxed">{section.content}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0B0F19] py-12 mt-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white font-chivo">OMBUDNOW</span>
            </div>
            <p className="text-slate-500 text-sm">
              © 2025 OmbudNow. {currentLang === 'en' ? 'All rights reserved.' : 'Todos os direitos reservados.'}
            </p>
            <div className="flex items-center gap-6">
              <Link to={currentLang === 'en' ? '/en/privacy-policy' : '/pt/politica-de-privacidade'} className="text-white text-sm transition-colors">
                {currentLang === 'en' ? 'Privacy' : 'Privacidade'}
              </Link>
              <Link to={paths.terms} className="text-slate-500 hover:text-white text-sm transition-colors">
                {currentLang === 'en' ? 'Terms' : 'Termos'}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
